import { NEEDS_ATTENTION_RATE_THRESHOLD } from "@/components/v2/BuildingSelector/constants";
import type { PresetKey } from "@/components/v2/DatePicker/constants";
import type { KpiNumberData } from "@/components/v2/KpiNumbers";
import {
  BottomTextView,
  comparisonKeys,
  getBottomTextView,
  getKpiValue,
} from "@/components/v2/KpiNumbers/utils";

import type { TOptions } from "i18next";
import type { GetTerminalAggregatesResponse } from "server/dwhApi/terminals/models.js";
import type { Terminal } from "server/dwhApi/terminals/types.js";

type Props = {
  currentData?: GetTerminalAggregatesResponse;
  comparisonData?: GetTerminalAggregatesResponse;
  datePreset?: PresetKey;
  presetHasComparison: boolean;
  t: (key: string, options?: TOptions) => string;
  terminalsData: Terminal[];
};

export const getKpiNumbersData = ({
  currentData,
  comparisonData,
  datePreset,
  presetHasComparison,
  t,
  terminalsData,
}: Props): KpiNumberData[] => {
  const hasNoComparisonData =
    !presetHasComparison || !currentData || !comparisonData;

  const comparisonKey = t(comparisonKeys[datePreset || ""]);

  if (!currentData) {
    return [];
  }

  return [
    {
      id: "recyclingRate",
      title: t("portfolioPage:kpiRecyclingRate"),
      getValue: () =>
        getKpiValue((currentData?.terminalAggregates.sortingDegree || 0) * 100),
      valueSuffix: "%",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            (currentData?.terminalAggregates.sortingDegree || 0) * 100,
          comparisonValue:
            (comparisonData?.terminalAggregates.sortingDegree || 0) * 100,
          comparisonKey,
          higherBetter: true,
          differenceSuffix: t("common:percentagePoints"),
        }),
    },
    {
      id: "totalWaste",
      title: t("portfolioPage:kpiTotalWaste"),
      getValue: () =>
        getKpiValue(currentData?.terminalAggregates.totalWasteWeight.quantity),
      valueSuffix: "kg",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            currentData?.terminalAggregates.totalWasteWeight.quantity || 0,
          comparisonValue:
            comparisonData?.terminalAggregates.totalWasteWeight.quantity || 0,
          comparisonKey,
          differenceSuffix: " kg",
        }),
    },
    {
      id: "generalWaste",
      title: t("portfolioPage:kpiGeneralWaste"),
      getValue: () =>
        getKpiValue(
          currentData?.terminalAggregates.totalResidualWasteWeight.quantity,
        ),
      valueSuffix: "kg",
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue:
            currentData?.terminalAggregates.totalResidualWasteWeight.quantity ||
            0,
          comparisonValue:
            comparisonData?.terminalAggregates.totalResidualWasteWeight
              .quantity || 0,
          comparisonKey,
          differenceSuffix: " kg",
        }),
    },
    {
      id: "nrBuildings",
      title: t("portfolioPage:kpiNumberBuildings"),
      getValue: () => terminalsData.length || 0,
      valueSuffix: "",
      getBottomText: () => {
        if (!terminalsData) return <></>;
        const totalNrNeedAttention =
          terminalsData.filter(
            (t) => t.sortingDegree < NEEDS_ATTENTION_RATE_THRESHOLD / 100,
          ).length || 0;

        const totalOnTrack = terminalsData.length - totalNrNeedAttention;

        return (
          <BottomTextView
            isPositive={false}
            bottomText={t("portfolioPage:kpiBuildingsBottomText", {
              totalNrNeedAttention,
              totalOnTrack,
            })}
          />
        );
      },
    },
    {
      id: "activation",
      title: t("portfolioPage:kpiActivation"),
      getValue: () => currentData.terminalAggregates.totalCustomers,
      valueSuffix: `/${currentData.terminalAggregates.totalCustomersAcrossClient}`,
      getBottomText: () =>
        getBottomTextView({
          hasNoComparisonData,
          currentValue: currentData?.terminalAggregates.totalCustomers || 0,
          comparisonValue:
            comparisonData?.terminalAggregates.totalCustomers || 0,
          comparisonKey,
          higherBetter: true,
          differenceSuffix: "",
        }),
    },
    {
      id: "wasteStreams",
      title: t("portfolioPage:kpiWasteStreams"),
      getValue: () => currentData.terminalAggregates.wasteTypes.length || 0,
      valueSuffix: "",
      getBottomText: () => <></>,
    },
  ];
};
